import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Container,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "../components/ThemeContext";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import BottomNavBar from "../components/BottomNavBar";
import { DeviceContext } from "../context/DeviceContext";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: "1.5rem 1.75rem",
  maxWidth: 860,
  margin: "0 auto",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.primary.main}22`,
  borderRadius: 16,
  boxShadow: "0 20px 45px rgba(0,0,0,0.30)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 10,
    "& fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.secondary.main,
    backgroundColor: "transparent !important",
    "&::placeholder": {
      color: `${theme.palette.text.secondary} !important`,
      opacity: 1,
      whiteSpace: "pre-line",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    paddingTop: "0 !important",
  },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 10000px ${theme.palette.background.default} inset !important`,
    WebkitTextFillColor: `${theme.palette.secondary.main} !important`,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.dark,
  //   marginTop: "2rem",
  "&:hover": {
    backgroundColor: theme.palette.background.darkGreen,
    color: theme.palette.text.primary,
  },
}));

const steps = [
  "About Me",
  "Philosophy",
  "Strategies",
  "Disclosures",
];

const stepPrompts = [
  "Tell people what you focus on and why they should follow you.",
  "Share the core principles behind your decisions.",
  "Describe your process, time horizon, and risk style.",
  "Add required disclosures and limitations.",
];

const stepExamples = [
  "Example: Retail investor focused on large-cap tech and earnings events.",
  "Example: Thesis-driven investing with disciplined position sizing.",
  "Example: Swing trades around catalysts, risk capped at 1-2% per position.",
  "Example: Educational content only, not financial advice. I may hold mentioned assets.",
];

function CreateUserInfoPage() {
  const { theme } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [aboutMe, setAboutMe] = useState("");
  const [investmentPhilosophy, setInvestmentPhilosophy] = useState("");
  const [investmentStrategies, setInvestmentStrategies] = useState("");
  const [disclaimers, setDisclaimers] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { isMobile } = useContext(DeviceContext); // Use isMobile from context
  const navigate = useNavigate();
  const { isLoggedIn, user } = useContext(AuthContext);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isLoggedIn) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/userinfo/getInfo`,
          {
            params: { username: user.username },
          }
        );
        const profile = response.data;
        setAboutMe(profile.aboutMe || "");
        setInvestmentPhilosophy(profile.investmentPhilosophy || "");
        setInvestmentStrategies(profile.investmentStrategies || "");
        setDisclaimers(profile.disclaimers || "");
      } catch (error) {
        console.info("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [isLoggedIn, user]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const profileData = {
        aboutMe,
        investmentPhilosophy,
        investmentStrategies,
        disclaimers,
      };

      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/userinfo/upsert`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(profileData),
        }
      );

      if (response.status === 200) {
        navigate(`/profile/${user.username}`);
      } else {
        setErrorMessage("Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrorMessage("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };
  const handleSkip = () => {
    if (activeStep === steps.length - 1) {
      navigate(`/profile/${user.username}`);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const getStepContent = (step) => {
    const fieldProps = {
      variant: "outlined",
      fullWidth: true,
      multiline: true,
      rows: isMobile ? 7 : 11,
      inputProps: { maxLength: 1200 },
    };

    const stepSubtitle = steps[step];

    const renderStepEditor = (value, setValue, prompt, example) => (
      <>
        <Typography
          variant="subtitle1"
          sx={{
            color: theme.palette.secondary.main,
            fontWeight: 700,
            textAlign: "center",
            mb: 1.2,
          }}>
          {stepSubtitle}
        </Typography>
        <StyledTextField
          label=""
          placeholder={`${prompt}\n${example}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          {...fieldProps}
          sx={{ mb: 1.2 }}
        />
      </>
    );

    switch (step) {
      case 0:
        return (
          <>
            {renderStepEditor(
              aboutMe,
              setAboutMe,
              stepPrompts[0],
              stepExamples[0]
            )}
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {aboutMe.length}/1200
            </Typography>
          </>
        );
      case 1:
        return (
          <>
            {renderStepEditor(
              investmentPhilosophy,
              setInvestmentPhilosophy,
              stepPrompts[1],
              stepExamples[1]
            )}
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {investmentPhilosophy.length}/1200
            </Typography>
          </>
        );
      case 2:
        return (
          <>
            {renderStepEditor(
              investmentStrategies,
              setInvestmentStrategies,
              stepPrompts[2],
              stepExamples[2]
            )}
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {investmentStrategies.length}/1200
            </Typography>
          </>
        );
      case 3:
        return (
          <>
            {renderStepEditor(
              disclaimers,
              setDisclaimers,
              stepPrompts[3],
              stepExamples[3]
            )}
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {disclaimers.length}/1200
            </Typography>
          </>
        );
      default:
        return "Unknown step";
    }
  };
  if (!isLoggedIn) {
    navigate("/signin");
    return null;
  }
  return (
    <>
      <Box
        sx={{
          pb: isMobile ? 2 : 2,
          pt: isMobile ? 0 : 0,
          mt: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        }}>
        <ResponsiveTopBar isMobile={isMobile} />
        <Container maxWidth="lg" sx={{ px: isMobile ? 1 : 2 }}>
          <Grid container spacing={isMobile ? 0 : 2} justifyContent="center">
            <Grid item xs={12} sm={10} justifyContent="center">
            <StyledPaper
              elevation={3}
              sx={{
                backgroundColor: theme.palette.background.paper,
              }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                gutterBottom
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  textAlign: "center",
                }}>
                Complete Your Profile
              </Typography>
              <Divider sx={{ mb: 2, borderColor: `${theme.palette.primary.main}33` }} />
              {isMobile ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={((activeStep + 1) / steps.length) * 100}
                    sx={{
                      mt: 0.8,
                      height: 6,
                      borderRadius: 999,
                      backgroundColor: `${theme.palette.primary.main}22`,
                      "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.primary.main },
                    }}
                  />
                </Box>
              ) : (
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
              <Box sx={{ mt: "1em" }}>
                {getStepContent(activeStep)}
                <Stack direction="row" sx={{ display: "flex", justifyContent: "space-between", mt: "1em", gap: 1 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ textTransform: "none", borderColor: `${theme.palette.primary.main}66`, borderRadius: 999 }}>
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading}
                      sx={{ textTransform: "none", minWidth: 120, borderRadius: 999 }}>
                      {loading ? <CircularProgress size={24} /> : "Save & Finish"}
                    </StyledButton>
                  ) : (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      sx={{ textTransform: "none", minWidth: 104, borderRadius: 999, px: 1.75 }}>
                      Continue
                    </StyledButton>
                  )}
                </Stack>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSkip}
                    sx={{ textTransform: "none", borderColor: `${theme.palette.primary.main}44`, borderRadius: 999 }}>
                    Skip for now
                  </Button>
                </Box>
              </Box>
              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            </StyledPaper>
            </Grid>
          </Grid>
        </Container>
        {isMobile && <BottomNavBar />}
      </Box>
    </>
  );
}

export default CreateUserInfoPage;
